import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../features/auth/hooks/useAuth";
import { useTheme } from "../contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import i18n from "../i18n/i18n";
import CondominioSelector from "./CondominioSelector";
import { getNotificaciones, marcarTodasLeidas, marcarLeida } from "../../features/portal/services/portalService";

const TIPO_ICON = {
  RESERVA: { icon: "fa-calendar-check", color: "#4ade80" },
  PAGO:    { icon: "fa-money-bill-transfer", color: "#f59e0b" },
  AVISO:   { icon: "fa-bell", color: "#38bdf8" },
  GENERAL: { icon: "fa-circle-info", color: "#a78bfa" },
};

export default function Header({ toggleSidebar }) {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLight     = theme === "light";
  const isContador  = localStorage.getItem("rol") === "CONTADOR";
  const isResidente = localStorage.getItem("rol") === "RESIDENTE";
  const isSuperAdmin = localStorage.getItem("is_superuser") === "true";

  const IC = isLight ? "#374151" : undefined;

  // ── Notificaciones ──────────────────────────────────────────────────────────
  const [notifs,   setNotifs]   = useState([]);
  const [unread,   setUnread]   = useState(0);
  const [open,     setOpen]     = useState(false);
  const dropRef = useRef(null);
  const pollRef = useRef(null);

  const fetchNotifs = useCallback(async () => {
    try {
      const data = await getNotificaciones();
      setNotifs(data.results || []);
      setUnread(data.unread || 0);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    fetchNotifs();
    pollRef.current = setInterval(fetchNotifs, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchNotifs]);

  // Cierra el dropdown al hacer click fuera
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  async function handleOpenBell() {
    setOpen(o => !o);
    if (!open && unread > 0) {
      try { await marcarTodasLeidas(); setUnread(0); setNotifs(prev => prev.map(n => ({ ...n, leida: true }))); }
      catch { /* silencioso */ }
    }
  }

  async function handleClickNotif(n) {
    setOpen(false);
    if (!n.leida) {
      try { await marcarLeida(n.id); } catch { /* silencioso */ }
    }
    if (n.link) {
      if (window.location.pathname === n.link) {
        // Already on this page — force a full route refresh so data reloads
        navigate(0);
      } else {
        navigate(n.link);
      }
    }
  }

  function handleLogout() { logout(); }

  function handleToggleLang() {
    const next = i18n.language === "es" ? "en" : "es";
    i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
  }

  const cardBg     = isLight ? "#ffffff" : "#1e2433";
  const cardBorder = isLight ? "rgba(0,0,0,0.10)" : "rgba(255,255,255,0.08)";
  const itemHover  = isLight ? "#f8fafc" : "#252c3d";
  const textPri    = isLight ? "#0f172a" : "#f1f5f9";
  const textSec    = isLight ? "#64748b" : "#94a3b8";

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&display=swap" rel="stylesheet" />
      <style>{`
        .notif-item:hover { background: ${itemHover} !important; }
        .notif-item-unread { border-left: 3px solid #f97316; }
        @keyframes notifSlideIn { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:none} }
      `}</style>

      <header>
        <nav>
          <div className="logoNav" style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <img src="/LOGO.png" alt="logo" width="32px" height="auto" style={{ flexShrink: 0 }} />
            <span style={{
              fontFamily: "'Montserrat', sans-serif", fontWeight: 800,
              fontSize: "1.2rem", letterSpacing: "2px", lineHeight: 1,
              userSelect: "none", textTransform: "uppercase", flexShrink: 0,
            }}>
              <span style={{ color: "#f97316" }}>COR</span>
              <span style={{ color: isLight ? "#1e293b" : "#e5ecf5" }}>VANA</span>
            </span>
            {(isContador || isSuperAdmin) && (
              <>
                <span style={{ width:1, height:22, flexShrink:0, background: isLight?"rgba(0,0,0,0.15)":"rgba(255,255,255,0.12)" }} />
                <CondominioSelector />
              </>
            )}
          </div>

          <div className="navIcons">
            {/* Hamburger */}
            <button className="nav-icon-btn nav-hamburger-btn" title={t("header.abrirMenu")}
              aria-label={t("header.abrirMenu")} onClick={toggleSidebar} style={{ color: IC }}>
              <i className="fa-solid fa-bars" style={{ color: IC }} />
            </button>

            {/* Idioma */}
            <button className="nav-icon-btn" title={t("header.cambiarIdioma")}
              aria-label={t("header.cambiarIdioma")} onClick={handleToggleLang}
              style={{ color: IC, fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.5px" }}>
              {i18n.language === "es" ? "EN" : "ES"}
            </button>

            {/* Tema */}
            <button className="nav-icon-btn" title={isLight ? t("header.temaOscuro") : t("header.temaClaro")}
              aria-label={isLight ? t("header.temaOscuro") : t("header.temaClaro")} onClick={toggleTheme} style={{ color: IC }}>
              <i className={`fa-solid ${isLight ? "fa-moon" : "fa-sun"}`} style={{ color: IC }} />
            </button>

            {/* Configuración — oculta para RESIDENTE */}
            {!isResidente && (
              <Link to="/settings" className="nav-icon-btn" title={t("header.configuracion")} style={{ color: IC }}>
                <i className="fa-solid fa-gear" style={{ color: IC }} />
              </Link>
            )}

            {/* Cerrar sesión */}
            <button className="nav-icon-btn nav-logout-btn" title={t("header.cerrarSesion")}
              aria-label={t("header.cerrarSesion")} onClick={handleLogout} style={{ color: IC }}>
              <i className="fa-solid fa-right-from-bracket" style={{ color: IC }} />
            </button>

            {/* ── Campanita ── */}
            <div ref={dropRef} style={{ position: "relative" }}>
              <button
                className="nav-bell-btn"
                onClick={handleOpenBell}
                title={t("header.notificaciones")}
                aria-label={t("header.notificaciones")}
                style={{ color: IC, position: "relative" }}
              >
                <i className="fa-solid fa-bell" style={{ color: IC }} />
                {unread > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -4,
                    background: "#f97316", color: "#fff",
                    borderRadius: "99px", fontSize: 9, fontWeight: 800,
                    minWidth: 16, height: 16, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    padding: "0 3px", lineHeight: 1, border: "2px solid var(--bg-nav, #0f172a)",
                  }}>
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </button>

              {/* Dropdown */}
              {open && (
                <div style={{
                  position: "absolute", top: "calc(100% + 10px)", right: 0,
                  width: 340, maxHeight: 460, overflowY: "auto",
                  background: cardBg, border: `1px solid ${cardBorder}`,
                  borderRadius: 14, zIndex: 9999,
                  boxShadow: isLight
                    ? "0 8px 32px rgba(0,0,0,0.14),0 2px 8px rgba(0,0,0,0.07)"
                    : "0 8px 40px rgba(0,0,0,0.55)",
                  animation: "notifSlideIn .18s ease both",
                }}>
                  {/* Header del dropdown */}
                  <div style={{
                    padding: "14px 16px 10px",
                    borderBottom: `1px solid ${cardBorder}`,
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: textPri }}>
                      {t("header.notificaciones")}
                    </span>
                    {notifs.some(n => !n.leida) && (
                      <button onClick={async () => {
                        try { await marcarTodasLeidas(); setUnread(0); setNotifs(prev => prev.map(n => ({ ...n, leida: true }))); }
                        catch { /* silencioso */ }
                      }} style={{
                        background: "none", border: "none", cursor: "pointer",
                        fontSize: 11, color: "#f97316", fontWeight: 700,
                      }}>
                        {t("header.marcarTodasLeidas")}
                      </button>
                    )}
                  </div>

                  {/* Lista */}
                  {notifs.length === 0 ? (
                    <div style={{ padding: "32px 16px", textAlign: "center", color: textSec }}>
                      <i className="fa-solid fa-bell-slash" style={{ fontSize: 28, opacity: 0.35, display: "block", marginBottom: 10 }} />
                      <p style={{ margin: 0, fontSize: 13 }}>{t("header.sinNotificaciones")}</p>
                    </div>
                  ) : (
                    notifs.map(n => {
                      const ti = TIPO_ICON[n.tipo] || TIPO_ICON.GENERAL;
                      return (
                        <div key={n.id} className={`notif-item${!n.leida ? " notif-item-unread" : ""}`}
                          onClick={() => handleClickNotif(n)}
                          style={{
                            display: "flex", alignItems: "flex-start", gap: 12,
                            padding: "12px 16px", cursor: n.link ? "pointer" : "default",
                            borderBottom: `1px solid ${cardBorder}`,
                            background: !n.leida ? (isLight ? "rgba(249,115,22,0.04)" : "rgba(249,115,22,0.06)") : "transparent",
                            transition: "background .15s",
                          }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                            background: `${ti.color}18`,
                            border: `1px solid ${ti.color}30`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <i className={`fa-solid ${ti.icon}`} style={{ color: ti.color, fontSize: 13 }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{
                              margin: "0 0 2px", fontSize: 13, fontWeight: n.leida ? 500 : 700,
                              color: textPri, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            }}>{n.titulo}</p>
                            {n.cuerpo && (
                              <p style={{
                                margin: "0 0 4px", fontSize: 12, color: textSec, lineHeight: 1.4,
                                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                              }}>{n.cuerpo}</p>
                            )}
                            <p style={{ margin: 0, fontSize: 11, color: textSec }}>
                              {new Date(n.created_at).toLocaleString(i18n.language, { dateStyle: "short", timeStyle: "short" })}
                            </p>
                          </div>
                          {!n.leida && (
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f97316", flexShrink: 0, marginTop: 4 }} />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </nav>
      </header>
    </>
  );
}
